import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2 } from 'lucide-react';

interface RecipeOutput {
  itemName: string;
  quantity: number;
  probability?: number;
}

interface RecipeOutputsEditorProps {
  outputs: RecipeOutput[];
  onChange: (outputs: RecipeOutput[]) => void;
}

export function RecipeOutputsEditor({ outputs, onChange }: RecipeOutputsEditorProps) {
  const addOutput = () => {
    onChange([...outputs, { itemName: '', quantity: 1 }]);
  };

  const removeOutput = (index: number) => {
    onChange(outputs.filter((_, i) => i !== index));
  };

  const updateOutput = (index: number, field: keyof RecipeOutput, value: string | number) => {
    const updated = outputs.map((output, i) =>
      i === index ? { ...output, [field]: value } : output,
    );
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium">Outputs</label>
        <Button type="button" variant="outline" size="sm" onClick={addOutput}>
          <Plus className="h-3 w-3 mr-1" />
          Add
        </Button>
      </div>
      {outputs.length === 0 && <p className="text-sm text-muted-foreground">No outputs defined.</p>}
      {outputs.map((output, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            placeholder="Item name"
            value={output.itemName}
            onChange={(e) => updateOutput(index, 'itemName', e.target.value)}
            className="flex-1"
          />
          <Input
            type="number"
            placeholder="Qty"
            value={output.quantity}
            onChange={(e) => updateOutput(index, 'quantity', parseInt(e.target.value, 10) || 1)}
            className="w-20"
            min={1}
          />
          <Input
            type="number"
            placeholder="Prob"
            value={output.probability ?? ''}
            onChange={(e) => {
              const val = parseFloat(e.target.value);
              updateOutput(index, 'probability', isNaN(val) ? 1 : val);
            }}
            className="w-20"
            min={0}
            max={1}
            step={0.01}
          />
          <Button type="button" variant="ghost" size="icon" onClick={() => removeOutput(index)}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ))}
    </div>
  );
}
